import { CancelFlowConfig, CancelFlowNode, CancelFlowNodeType } from './types';
import type { RetentionDiscountNode, SkuSwapNode } from './types';
import { AppState } from './hooks';
import { requestCancelSubscription } from './smi-bridge';

export type CancelFlowAction =
  | {
      type: 'next' | 'accepted' | 'reset' | 'back';
    }
  | {
      type: 'cancel-reason';
      reasonCode: string;
      cancelReason: string;
    };

export type CancelFlowContext = {
  subscriptionId: string;
  cancelReason?: string;
  /** ID of the cancel flow config being run, included as an attribute on the flow's tracking events */
  cancelFlowId?: string;
};

export async function handleAction(
  currentNode: CancelFlowNode | null,
  previousNode: CancelFlowNode | null,
  action: CancelFlowAction,
  config: CancelFlowConfig,
  context: CancelFlowContext,
  state: AppState
): Promise<{ nextStep: CancelFlowNode | null; context: CancelFlowContext } | null> {
  // Store cancel reason in context when selected
  let newContext = { ...context };

  if (action.type === 'cancel-reason') {
    newContext.cancelReason = action.cancelReason;
  }

  let nextNodeName = determineNextNode(currentNode, previousNode, action, config, state, context);
  let nextNode = nextNodeName ? getNodeByName(config, nextNodeName) : null;
  if (!nextNode) return null;

  try {
    nextNode = await handleExecutableNode(nextNode, newContext, config);
  } catch {
    // if an error happens, stay on the current node
    nextNode = currentNode;
  }

  return { nextStep: nextNode, context: newContext };
}

export function determineNextNode(
  currentNode: CancelFlowNode | null,
  previousNode: CancelFlowNode | null,
  action: CancelFlowAction,
  config: CancelFlowConfig,
  state: AppState,
  context: CancelFlowContext
): string | null {
  if (action.type === 'back') {
    // when we start having more executable nodes, we might need this to recursively traverse back
    // i.e. we need a stack of previous nodes
    // TODO match current SMI implementation where we store an array so you can go back multiple times
    return previousNode?.name || null;
  } else if (action.type === 'reset') {
    return getInitialStep(config, state, context)?.name || null;
  }

  if (!currentNode) {
    return null;
  }

  let nextNodeName: string | null = null;

  switch (currentNode.type) {
    case 'cancel-flow:start':
      nextNodeName = currentNode.parameters.routing.next;
      break;
    case 'cancel-flow:survey':
      if (action.type === 'cancel-reason') {
        nextNodeName = currentNode.parameters.routing?.[action.reasonCode];
      }
      break;
    case 'cancel-flow:skip':
    case 'cancel-flow:sku-swap':
    case 'cancel-flow:pause':
    case 'cancel-flow:retention-discount':
    case 'cancel-flow:benefit-reminder':
    case 'cancel-flow:custom-content':
      if (action.type === 'accepted' || action.type === 'next') {
        nextNodeName = currentNode.parameters.routing?.[action.type] || null;
      }
      break;
    default:
      nextNodeName = currentNode.parameters.routing.next || null;
  }

  if (nextNodeName) {
    const nextNode = getNodeByName(config, nextNodeName);
    if (nextNode) {
      // if we're advancing to a node that requires an eligibility check
      const eligibilityResult = checkStepEligibility(nextNode, state, context);
      if (eligibilityResult.requiresCheck && !eligibilityResult.isEligible) {
        // user not eligible: try node-level ineligible, then config-level default, then node's next
        const routing = eligibilityResult.node.parameters.routing;
        if (routing.ineligible) {
          return routing.ineligible;
        }
        if (routing.ineligible === undefined && config.defaults?.ineligible) {
          return config.defaults.ineligible;
        }
        return routing.next || null;
      }
    }
  }

  return nextNodeName;
}

export function getNodeByName(config: CancelFlowConfig, nodeName: string | null): CancelFlowNode | null {
  return config.nodes.find(node => node.name === nodeName) || null;
}

const executableNodeTypes: CancelFlowNodeType[] = ['cancel-flow:cancel-subscription'];

async function handleExecutableNode(
  node: CancelFlowNode,
  context: CancelFlowContext,
  config: CancelFlowConfig
): Promise<CancelFlowNode | null> {
  if (!executableNodeTypes.includes(node.type)) {
    return node;
  }

  let nextNodeName: string | null = null;
  if (node.type === 'cancel-flow:cancel-subscription') {
    await requestCancelSubscription({
      subscription_id: context.subscriptionId,
      cancel_reason: context.cancelReason,
      // this is the one event of the flow that we don't fire ourselves, so the ID has to travel through the saga layer
      // instead of being added by `useTrack`
      ...(context.cancelFlowId && { tracking_context: { cancelFlowId: context.cancelFlowId } })
    });
    nextNodeName = node.parameters.routing.next || null;
  }

  return nextNodeName ? getNodeByName(config, nextNodeName) : null;
}

export const getInitialStep = (
  cancelFlowConfig: CancelFlowConfig,
  state: AppState,
  context: CancelFlowContext
): CancelFlowNode | null => {
  const startNode = cancelFlowConfig.nodes.find(node => node.type === 'cancel-flow:start');
  if (!startNode) return null;

  // we want to go through the same logic as when "next" is clicked
  // this is so we run eligibility checks if we start with a node like retention discount (mostly useful for the tests, but technically a valid config)
  const nextNodeName = determineNextNode(startNode, null, { type: 'next' }, cancelFlowConfig, state, context);
  return getNodeByName(cancelFlowConfig, nextNodeName);
};

type EligibilityCheckNode = SkuSwapNode | RetentionDiscountNode;
export type EligibilityCheckNodeType = EligibilityCheckNode['type'];

// Define state checks for steps that require eligibility checks before being shown
const eligibilityCheckers: Record<EligibilityCheckNodeType, (state: AppState, context: CancelFlowContext) => boolean> =
  {
    'cancel-flow:retention-discount': state => {
      const retentionIncentive = state.retention_incentives?.default_retention_incentive;
      return retentionIncentive ? retentionIncentive.target === 'item' : false;
    },
    'cancel-flow:sku-swap': (state, context) => {
      // First check: Must have alternative products available
      const hasAlternativeProducts = state.sku_swap.subscriptions?.[context.subscriptionId]?.length > 0;
      if (!hasAlternativeProducts) {
        return false;
      }

      // Second check: Currency must match merchant's primary currency
      // Allow if: order has no currency (null/undefined) OR order currency matches merchant currency
      const orderItem = state.order_items?.find(oi => oi.subscription === context.subscriptionId);
      const order = orderItem ? state.orders?.find(o => o.public_id === orderItem.order) : null;

      if (!order) {
        return false;
      }

      const merchantCurrencyCode = state.settings?.currency_code;
      const orderCurrencyCode = order.currency_code;

      const isCurrencyMatch = !merchantCurrencyCode || !orderCurrencyCode || orderCurrencyCode === merchantCurrencyCode;

      return isCurrencyMatch;
    }
  };

function isEligibilityCheckNode(node: CancelFlowNode): node is EligibilityCheckNode {
  return node.type in eligibilityCheckers;
}

function checkStepEligibility(
  node: CancelFlowNode,
  state: AppState,
  context: CancelFlowContext
): { requiresCheck: false } | { requiresCheck: true; isEligible: boolean; node: EligibilityCheckNode } {
  if (!isEligibilityCheckNode(node)) {
    return { requiresCheck: false };
  }
  // node is returned for type-narrowing purposes
  return { requiresCheck: true, isEligible: eligibilityCheckers[node.type](state, context), node };
}
